'use client'

import { useState, useMemo } from 'react'

import { useUserProfile } from '../../hooks/useUserProfile'
import { useUserTitles } from '../../hooks/useUserTitles'

import { useUserProgress } from '@/shared/contexts/UserProgress'
import { cn } from '@/shared/lib/utils'
import { api } from '@/shared/services/api'
import type { Title } from '@/shared/types/user'
import { Button } from '@/shared/ui/Button'
import { Card, CardBody, CardFooter, CardHeader, CardTitle } from '@/shared/ui/Card'
import { Page } from '@/shared/ui/Page'

function getCurrentTitle(progressTitle: unknown, profileTitle: Title | null): Title | null {
  if (profileTitle && typeof profileTitle === 'object' && 'name' in profileTitle) {
    return profileTitle
  }
  if (progressTitle && typeof progressTitle === 'object' && 'name' in progressTitle) {
    return progressTitle as Title
  }
  return null
}

export function TitlesPage() {
  const { title: progressTitle, refetchProgress } = useUserProgress()
  const { userProfile, refetchUserProfile } = useUserProfile({ me: true })
  const { records: titles, isLoading, isError } = useUserTitles()
  const [selectedTitle, setSelectedTitle] = useState<Title | null>(null)
  const [isSaving, setIsSaving] = useState(false)

  const currentTitle = useMemo(
    () => getCurrentTitle(progressTitle, userProfile?.title ?? null),
    [progressTitle, userProfile?.title]
  )

  const sortedTitles = useMemo(
    () => [...titles].sort((a, b) => b.points - a.points),
    [titles]
  )

  const hasChanges =
    selectedTitle != null &&
    (currentTitle == null || selectedTitle.name !== currentTitle.name)

  const handleSelect = (title: Title) => {
    setSelectedTitle(title)
  }

  const handleSave = async () => {
    const titleId =
      selectedTitle?.id ??
      sortedTitles.find(t => t.name === selectedTitle?.name)?.id
    if (titleId == null) return

    setIsSaving(true)
    try {
      await api.put('/user/title', { title_id: titleId })
      await Promise.all([refetchProgress(), refetchUserProfile()])
      setSelectedTitle(null)
    } catch (error) {
      console.error(error)
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <Page variant="default">
      <Card variant="default" className="max-w-2xl mx-auto">
        <CardHeader>
          <CardTitle>Títulos</CardTitle>
        </CardHeader>
        <CardBody>
          {currentTitle && (
            <p className="text-sm text-[#6B6883] mb-4">
              Seu título atual: <span className="font-semibold text-primary-dark">{currentTitle.name}</span>
            </p>
          )}
          {isError && (
            <p className="rounded-2xl border border-dashed border-[#E0D7FF] bg-white/80 px-4 py-6 text-center text-sm text-[#6B6883]">
              Não foi possível carregar seus títulos
            </p>
          )}
          {isLoading && !sortedTitles.length && (
            <p className="rounded-2xl border border-dashed border-[#E0D7FF] bg-white/80 px-4 py-6 text-center text-sm text-[#6B6883]">
              Carregando títulos...
            </p>
          )}
          {!isLoading && !isError && !sortedTitles.length && (
            <p className="rounded-2xl border border-dashed border-[#E0D7FF] bg-white/80 px-4 py-6 text-center text-sm text-[#6B6883]">
              Você ainda não conquistou títulos
            </p>
          )}
          {!isLoading && sortedTitles.length > 0 && (
            <ul className="space-y-2">
              {sortedTitles.map(title => {
                const isActive =
                  currentTitle != null && title.name === currentTitle.name
                const isSelected =
                  selectedTitle != null && title.name === selectedTitle.name

                return (
                  <li key={`${title.name}-${title.points}`}>
                    <button
                      type="button"
                      onClick={() => handleSelect(title)}
                      className={cn(
                        'flex w-full items-center justify-between rounded-xl px-4 py-3 text-left transition-all duration-100',
                        'bg-white bg-[linear-gradient(360deg,_#EAEEFF_-12.43%,_#BAC4F0_143.3%)]',
                        'shadow-[0_4px_0_-1px_#6A81DB,inset_0_3px_0_0_rgba(255,255,255,0.25)]',
                        'hover:-translate-y-px hover:shadow-[0_5px_0_-1px_#6A81DB,inset_0_3px_0_0_rgba(255,255,255,0.25)]',
                        'active:translate-y-px active:shadow-[inset_0_3px_0_0_rgba(255,255,255,0.25)]',
                        isActive && [
                          '!bg-[#FFF5ED] !bg-[linear-gradient(360deg,_#FFF9F5_-12.43%,_#FFE8D8_143.3%)]',
                          '!shadow-[0_4px_0_-1px_#C75300,inset_0_3px_0_0_rgba(255,255,255,0.35)]',
                          'text-[#C75300]'
                        ],
                        isSelected && !isActive && 'ring-2 ring-primary ring-offset-2 ring-offset-transparent'
                      )}
                    >
                      <span className={cn(
                        'font-semibold',
                        isActive ? 'text-[#C75300]' : 'text-[#1F1C2B]'
                      )}>
                        {title.name}
                        {isActive && (
                          <span className="ml-2 text-xs font-semibold uppercase text-[#C75300]">
                            Atual
                          </span>
                        )}
                      </span>
                      <span className={cn(
                        'text-sm',
                        isActive ? 'text-[#C75300]/80' : 'text-[#7F7FAD]'
                      )}>
                        {title.points} pts
                      </span>
                    </button>
                  </li>
                )
              })}
            </ul>
          )}
        </CardBody>
        {hasChanges && (
          <CardFooter>
            <Button
              className="w-full"
              onClick={handleSave}
              disabled={isSaving}
            >
              {isSaving ? 'Salvando...' : 'Salvar'}
            </Button>
          </CardFooter>
        )}
      </Card>
    </Page>
  )
}
